import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface SlidePanelProps {
  open: boolean;
  title: string;
  width?: number;
  // Fired on overlay click, close-button, or Escape. Caller owns dirty-check
  // / discard-confirmation logic — this primitive stays presentational.
  onClose: () => void;
  children: ReactNode;
}

export function SlidePanel({
  open,
  title,
  width = 480,
  onClose,
  children,
}: SlidePanelProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape closes when open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // a11y: remove the off-screen panel from focus/AT trees when closed.
  // `inert` is set imperatively because @types/react 18 doesn't type the
  // prop yet (React 19+ adds it natively). Browsers without `inert` support
  // (very old) just ignore the attribute — the panel stays keyboard-reachable,
  // which is a degraded but functional fallback.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (open) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }
  }, [open]);

  // Auto-focus first focusable element on open. 50 ms lets the slide-in
  // animation start so the focused field is already visible.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      const first = bodyRef.current?.querySelector<HTMLElement>(
        'input, select, textarea, button:not([disabled])',
      );
      first?.focus();
    }, 50);
    return () => window.clearTimeout(id);
  }, [open]);

  return createPortal(
    <>
      <div
        className={`slide-panel-overlay${open ? ' slide-panel-overlay--visible' : ''}`}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={`slide-panel${open ? ' slide-panel--open' : ''}`}
        // CSS `min()` clamps the panel to the viewport on narrow screens so
        // wide panels (e.g. the 780 px Management Guidelines form) never
        // overflow off-screen. Numeric `width` prop is preserved as the
        // maximum.
        style={{ width: `min(${width}px, 100vw)` }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="slide-panel__header">
          <h2 className="slide-panel__title">{title}</h2>
          <button
            type="button"
            className="slide-panel__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="slide-panel__body" ref={bodyRef}>
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
}
